import { MarginPercentage } from '@plato/ui-legacy/components/list-views/margin-percentage';
import { toCurrencyString, toLocaleString } from '@plato/ui-legacy/lib/currency-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import {
    Card,
    CardActions,
    CardActionsGroup,
    CardContent,
    CardExpandAction,
    CardExpandContent,
    CardFront,
    CardHeader,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { Button } from '@plato/ui/primitives/button';
import { Skeleton } from '@plato/ui/primitives/skeleton';
import { FileIcon, PlusIcon, UserIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MaskedContent } from '../../customer-mode/components/masked-content.tsx';

/** The top positions are shown in full; the rest collapse into one summed row. */
const visiblePositionLimit = 5;

export type LogInteractionOfferPosition = {
    id: string;
    description: string;
    quantity: number;
    total: number;
};

export type LogInteractionOfferExpansion = {
    /** From the list row, so the summary line renders before the positions arrive. */
    positionsCount: number;
    positions: LogInteractionOfferPosition[];
    /** Absolute gross margin; the percentage is derived against the card's amount. */
    margin?: number;
    creator?: string;
    kommission?: string;
    isPending?: boolean;
};

export type LogInteractionOfferCardProps = {
    title: string;
    stateLabel: string;
    stateTone: 'warning' | 'weak';
    amount: number;
    /** Unsent draft — dashed outline, muted total, and nothing to expand. */
    draft?: boolean;
    /** Omitted for drafts, which stay a dense single row. */
    expansion?: LogInteractionOfferExpansion;
    /** Renders the card already unfolded — the expanded state as a starting point. */
    defaultExpanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    /** Opens the create-task form. Omitted where the offer takes no actions, as on a draft. */
    onCreateTask?: () => void;
};

export function LogInteractionOfferCard({
    title,
    stateLabel,
    stateTone,
    amount,
    draft = false,
    expansion,
    defaultExpanded = false,
    onExpandedChange,
    onCreateTask,
}: LogInteractionOfferCardProps): ReactNode {
    const { t } = useTranslation('notes');
    const locale = useResolvedLocale();

    return (
        <Card
            className={draft ? 'border-dashed' : undefined}
            defaultOpen={defaultExpanded}
            onOpenChange={onExpandedChange}
        >
            <CardFront>
                <CardStart>
                    <FileIcon />
                </CardStart>
                <CardContent>
                    <CardHeader className="flex-nowrap">
                        <CardTitle className="min-w-0 flex-1 truncate">{title}</CardTitle>
                        <CardSubtitle className="shrink-0 items-center whitespace-nowrap tabular-nums">
                            <span className={stateTone === 'warning' ? 'text-warning' : 'text-content-weak'}>
                                {stateLabel}
                            </span>
                            <span>·</span>
                            <span className={draft ? undefined : 'text-content'}>
                                {toCurrencyString(amount, locale)}
                            </span>
                        </CardSubtitle>
                    </CardHeader>
                </CardContent>
                {expansion && <CardExpandAction />}
            </CardFront>
            {expansion && (
                <>
                    <CardExpandContent>
                        <div className="flex min-w-0 grow flex-col gap-container-cozy">
                            <div className="flex items-baseline gap-container-cozy">
                                <span className="min-w-0 flex-1 truncate text-body text-content-weak tabular-nums">
                                    {t('notes__offers_positions_count', {
                                        count: expansion.positionsCount,
                                    })}
                                </span>
                                {expansion.margin !== undefined && (
                                    <MaskedContent>
                                        <span className="shrink-0 whitespace-nowrap text-body text-content-weak tabular-nums">
                                            {t('notes__offers_margin_label')}{' '}
                                            <MarginPercentage margin={expansion.margin} revenue={amount} />
                                            {' · '}
                                            {toCurrencyString(expansion.margin, locale)}
                                        </span>
                                    </MaskedContent>
                                )}
                            </div>
                            {expansion.isPending ? (
                                <div className="flex flex-col gap-container-cozy py-container-tight">
                                    <Skeleton className="w-full" />
                                    <Skeleton className="w-4/5" />
                                    <Skeleton className="w-3/5" />
                                </div>
                            ) : (
                                <PositionList positions={expansion.positions} />
                            )}
                            {(expansion.creator !== undefined || expansion.kommission !== undefined) && (
                                <div className="flex items-center gap-container-tight text-ui-sm text-content-weak">
                                    {expansion.creator && (
                                        <>
                                            <UserIcon className="size-3.5 shrink-0" />
                                            <span className="truncate tabular-nums">{expansion.creator}</span>
                                        </>
                                    )}
                                    {expansion.creator && expansion.kommission && <span>·</span>}
                                    {expansion.kommission && (
                                        <span className="truncate tabular-nums">
                                            {t('notes__offers_kommission', { number: expansion.kommission })}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </CardExpandContent>
                    {onCreateTask && (
                        <CardActions>
                            <CardActionsGroup className="grow justify-end">
                                <Button variant="primary" onClick={onCreateTask}>
                                    <PlusIcon /> {t('notes__offers_task_action')}
                                </Button>
                            </CardActionsGroup>
                        </CardActions>
                    )}
                </>
            )}
        </Card>
    );
}

type PositionListProps = {
    positions: LogInteractionOfferPosition[];
};

// Biggest positions first; whatever doesn't fit collapses into one summed row.
function PositionList({ positions }: PositionListProps): ReactNode {
    const { t } = useTranslation('notes');
    const locale = useResolvedLocale();

    if (positions.length === 0) {
        return null;
    }

    const sorted = positions.toSorted((a, b) => b.total - a.total);
    const visible = sorted.slice(0, visiblePositionLimit);
    const trimmed = sorted.slice(visiblePositionLimit);
    const trimmedTotal = trimmed.reduce((total, position) => total + position.total, 0);

    return (
        // Subgrid keeps the quantity column as wide as its longest value across all rows.
        <div className="grid grid-cols-[minmax(2rem,auto)_minmax(0,1fr)_auto] gap-x-container-cozy divide-y divide-line-weak">
            {visible.map((position) => (
                <div
                    key={position.id}
                    className="col-span-3 grid grid-cols-subgrid items-center py-container-tight"
                >
                    <span className="whitespace-nowrap text-right text-body text-content-weak tabular-nums">
                        {toLocaleString(position.quantity, locale)}×
                    </span>
                    <span className="truncate text-body">{position.description}</span>
                    <span className="whitespace-nowrap text-right text-body text-content-weak tabular-nums">
                        {toCurrencyString(position.total, locale)}
                    </span>
                </div>
            ))}
            {trimmed.length > 0 && (
                <div className="col-span-3 grid grid-cols-subgrid items-center py-container-tight">
                    <span />
                    <span className="truncate text-body text-content-weak">
                        {t('notes__offers_positions_more', { count: trimmed.length })}
                    </span>
                    <span className="whitespace-nowrap text-right text-body text-content-weak tabular-nums">
                        {toCurrencyString(trimmedTotal, locale)}
                    </span>
                </div>
            )}
        </div>
    );
}
